import type { PartialDeep } from 'type-fest';
import { type AssetDataResponse, type SyncResponse, VehicleType2 } from '../../../src/store/facade/facadeApiV3';

const generateRandomAssetName = (length: number) => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
        counter += 1;
    }
    return result;
};
const allowedAssetTypes = [
    VehicleType2.Truck,
    VehicleType2.Bus,
    VehicleType2.Van,
    VehicleType2.Trailer,
    VehicleType2.Undefined,
];

export const generateAssetMock = (): PartialDeep<AssetDataResponse> => ({
    asset_id: '6da2b1b1-b714-4af7-9c57-c81b8dc3519a',
    account_id: '237c58e3-60b5-4442-a9ec-8641ea39325b',
    name: generateRandomAssetName(21),
    vehicleType: allowedAssetTypes[Math.floor(Math.random() * allowedAssetTypes.length)],
    brand: 'MAN',
    model: 'eTGX',
    occurred_at: '2024-01-19T11:45:26.951Z',
    temperature: -15,
    position: {
        latitude: 65.61075,
        longitude: 18.72318,
        altitude: 440.0,
        speed: 83.0,
    },
    state: {
        mileage: 74489,
        ignition_on: true,
        fuel_level: 28,
        remaining_range: 300,
        weight_total: 53719,
        state_of_charge: 50,
        energy_level: 200,
    },
    details: {
        fuel_type: 'electric',
        number_of_axes: 3,
        tires: '315/70 R22.5',
        details_bev: true,
        curbWeight: 9500,
    },
    driver_state: {
        driving_and_resting_details: {
            remaining_current_driving_time: 15000,
        },
    },
    weather: {
        description: 'Sunny. Frigid.',
        sky_description: 'Sunny',
        humidity: '85',
        temperature_summary: {
            temperature: -15,
            temperature_description: 'Frigid',
            high_temperature: '-15.50',
            low_temperature: '-25.10',
        },
        wind_summary: {
            air_description: '',
            wind_speed: 12,
            wind_description: 'West',
            wind_short_description: 'W',
        },
    },
    batteryConfiguration: {
        batteryPacksNumber: 3,
        chargePortType: ['CCS', 'MCS'],
        maxChargingCurrent: 600,
        energyManagementType: 'BMS',
        maxPower: 375,
        maxRegenPower: 250,
    },
    axles: [
        { number: 1, ratio: 3.7 },
        { number: 2, ratio: 3.7 },
        { number: 3, ratio: 1.0 },
    ],
    vehicleProfileId: 'a0e1890c-69cf-4060-8ba0-c1bc4f966a8e', // man-etgx-4x2-tractor-semitrailer
});

export const generateAssetMockWithoutProfile = (): PartialDeep<AssetDataResponse> => ({
    ...generateAssetMock(),
    vehicleProfileId: null,
});

export const generateAssetMockWithUnknownProfile = (): PartialDeep<AssetDataResponse> => ({
    ...generateAssetMock(),
    vehicleProfileId: 'non-existent-profile-id',
});

/**
 * Mock for a 202 response when sending a tour to an offline vehicle.
 * The API returns this SyncResponse body indicating the tour has been
 * queued for deferred sync and will be delivered when the vehicle is back on.
 */
export const vehicleOfflineSyncResponseMock: SyncResponse = {
    message: 'Vehicle is offline, tour queued for deferred sync',
    reason: 'VEHICLE_OFFLINE' as SyncResponse['reason'],
    tourId: 'ddcdffec-9666-4c39-ba8e-f66505fa3207',
};
